import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import DeleteModal from "@via/components/delete-modal/delete-modal.tsx";
import Table from "@via/components/table/table.tsx";
import { formatDateTime } from "@via/pages/account/admin/notification-settings/utils/date-helpers.ts";
import { type ColumnDef } from "@tanstack/react-table";
import { SystemBannerWithCreator } from "common";
import { type JSX, useCallback, useMemo, useState } from "react";

// Adapter type to make SystemBannerWithCreator compatible with DeleteModal
type BannerDeleteItem = {
  id: number;
  name: string;
  originalBanner: SystemBannerWithCreator;
};

interface BannerHistoryProps {
  banners: SystemBannerWithCreator[];
  total: number;
  isLoading: boolean;
  isDeleting: boolean;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onEdit: (banner: SystemBannerWithCreator) => void;
  onDelete: (id: number) => void;
}

export default function BannerHistory({
  banners,
  total,
  isLoading,
  isDeleting,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
}: BannerHistoryProps): JSX.Element {
  const [bannerToDelete, setBannerToDelete] = useState<BannerDeleteItem | null>(
    null
  );

  const handleDeleteClick = useCallback((banner: SystemBannerWithCreator) => {
    setBannerToDelete({
      id: banner.id,
      name: banner.primaryMessage,
      originalBanner: banner,
    });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (bannerToDelete) {
      onDelete(bannerToDelete.id);
      setBannerToDelete(null);
    }
  }, [bannerToDelete, onDelete]);

  const columns: ColumnDef<SystemBannerWithCreator>[] = useMemo(
    () => [
      {
        id: "status",
        header: () => "Status",
        cell: ({ row }) => (
          <div className="flex items-center">
            {row.original.isEnabled ? (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                Active
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                Inactive
              </span>
            )}
          </div>
        ),
        size: 80,
      },
      {
        id: "primaryMessage",
        header: () => "Primary Message",
        cell: ({ row }) => (
          <div
            className="max-w-xs truncate text-sm text-gray-900"
            title={row.original.primaryMessage}>
            {row.original.primaryMessage}
          </div>
        ),
        size: 200,
      },
      {
        id: "secondaryMessage",
        header: () => "Secondary Message",
        cell: ({ row }) => (
          <div
            className="max-w-xs truncate text-sm text-gray-600"
            title={row.original.secondaryMessage}>
            {row.original.secondaryMessage}
          </div>
        ),
        size: 200,
      },
      {
        id: "schedule",
        header: () => "Schedule",
        cell: ({ row }) => (
          <div className="text-xs text-gray-500">
            <div>{formatDateTime(row.original.startDatetime)}</div>
            <div className="text-gray-400">to</div>
            <div>{formatDateTime(row.original.endDatetime)}</div>
          </div>
        ),
        size: 180,
      },
      {
        id: "createdAt",
        header: () => "Created",
        cell: ({ row }) => (
          <div className="text-xs text-gray-500">
            <div>{formatDateTime(row.original.createdAt)}</div>
            <div className="text-gray-400">
              by {row.original.createdByEmail || "Unknown"}
            </div>
          </div>
        ),
        size: 150,
      },
      {
        id: "actions",
        header: () => "Actions",
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                onEdit(row.original);
              }}
              className="text-indigo-500 hover:text-indigo-700 p-1 rounded hover:bg-indigo-50"
              title="Edit"
              aria-label={`Edit notification: ${row.original.primaryMessage}`}>
              <PencilIcon className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                handleDeleteClick(row.original);
              }}
              className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
              title="Delete"
              aria-label={`Delete notification: ${row.original.primaryMessage}`}>
              <TrashIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        ),
        size: 80,
      },
    ],
    [onEdit, handleDeleteClick]
  );

  return (
    <>
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Notification History
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          View and manage previously configured notifications
        </p>

        <div className="mt-4">
          <Table
            data={banners}
            columns={columns}
            isLoading={isLoading}
            page={page}
            setPage={onPageChange}
            pageSize={pageSize}
            setPageSize={onPageSizeChange}
            total={total}
            showPagination={true}
            pageSizeOptions={[10, 20, 40]}
            emptyMessage="No notification history available."
            enableSorting={false}
          />
        </div>
      </div>

      <DeleteModal
        item={bannerToDelete}
        setItem={setBannerToDelete}
        deleteFunction={handleConfirmDelete}
        isLoading={isDeleting}
        title="Delete Notification Banner"
        message={name => (
          <>
            Are you sure you want to delete the notification banner{" "}
            <strong className="font-semibold">&#34;{name}&#34;</strong>? This
            action cannot be undone.
          </>
        )}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
}
